import { Alert, Button, Form, Input, Radio, Spin, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { subManagementService } from "@/api/subManagementService";
import { Breadcrumb } from "@/components/common";

interface EditForm {
    roles: number[];
    receiptSms: number;
    accountState: number;
}
interface Detail {
    account?: { realName?: string; mobile?: string };
    customerEnterprise?: { roles?: number[]; permissions?: number[]; receiptSms?: number; accountState?: number };
}

/** 编辑子账号仅允许调整短信接收和账号状态，姓名手机号保持只读。 */
export function SubAccountEditPage() {
    const [form] = Form.useForm<EditForm>();
    const [detail, setDetail] = useState<Detail>({});
    const [roles, setRoles] = useState<number[]>([]);
    const [permissions, setPermissions] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = Number(searchParams.get("id") || 0);
    /** 加载详情并保留角色列表，避免保存状态时覆盖原授权角色。 */
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const data = (await subManagementService.getSubAccountDetail(id)) as any;
            setDetail(data);
            setRoles(data.customerEnterprise?.roles ?? []);
            setPermissions(data.customerEnterprise?.permissions ?? []);
            form.setFieldsValue({
                receiptSms: data.customerEnterprise?.receiptSms ?? 0,
                accountState: data.customerEnterprise?.accountState ?? 0
            });
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 表单通过后保存，并返回子账号详情页。 */
    const save = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            await subManagementService.editSubAccount(id, { ...values, roles, permissions });
            void message.success("已保存");
            navigate(-1);
        } catch {
            void message.error("保存失败，请稍后重试");
        } finally {
            setSaving(false);
        }
    };
    if (loading) return <Spin fullscreen tip="子账号信息加载中" />;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "子账号信息加载失败" : "缺少子账号 ID"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    return (
        <section className="sub-account-edit-page">
            <div className="section-card sub-account-edit-card">
                <Breadcrumb
                    routes={[
                        { name: "账号管理" },
                        { name: "子账号管理", path: "/me/subAccountManagement" },
                        { name: "编辑子账号" }
                    ]}
                />
                <div className="sub-account-edit-title">编辑子账号</div>
                <Form form={form} labelCol={{ flex: "auto" }}>
                    <div className="sub-account-edit-sub-title">子账号信息</div>
                    <div className="sub-account-edit-grid">
                        <Form.Item label="姓名">
                            <Input disabled value={detail.account?.realName} />
                        </Form.Item>
                        <Form.Item label="手机号">
                            <Input disabled value={detail.account?.mobile} />
                        </Form.Item>
                        <Form.Item name="receiptSms" label="是否接收短信" required>
                            <Radio.Group>
                                <Radio value={1}>是</Radio>
                                <Radio value={0}>否</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item name="accountState" label="账号状态" required>
                            <Radio.Group>
                                <Radio value={1}>已激活</Radio>
                                <Radio value={0}>已失效</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <div className="sub-account-edit-footer">
                <Button type="primary" loading={saving} onClick={() => void save()}>
                    保存
                </Button>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
        </section>
    );
}
